import React, { useState, useEffect } from 'react';
import { Button, Row, Col, Form } from '@/components/ui';
import { SearchableDropdown } from '../../../components/SearchableDropdown';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import FiltersCard from '../../../components/FiltersCard';
import { AssetFilterOptions } from '../../../services/assetService';
import { ASSET_TYPE_LABELS } from '../../../constants/assetTypes';

interface AssetFiltersProps {
  filterOptions: AssetFilterOptions;
  filters: {
    serviceAreaId: string;
    contractAreaId: string;
    assetType: string;
    search: string;
  };
  onApplyFilters: (filters: {
    serviceAreaId: string;
    contractAreaId: string;
    assetType: string;
    search: string;
  }) => void;
  onClearFilters?: () => void;
}

const AssetFilters: React.FC<AssetFiltersProps> = ({
  filterOptions,
  filters,
  onApplyFilters,
  onClearFilters,
}) => {
  
  // Local state for pending filters (not yet applied)
  const [pendingFilters, setPendingFilters] = useState(filters);

  // Update pending filters when applied filters change (e.g., after clear)
  useEffect(() => {
    setPendingFilters(filters);
  }, [filters]);



  // Check if any filter is applied
  const hasActiveFilters = () => {
    return filters.serviceAreaId !== '' ||
           filters.contractAreaId !== '' ||
           filters.assetType !== '';
  };

  // Check if pending filters are different from applied filters
  const hasUnappliedChanges = () => {
    return pendingFilters.serviceAreaId !== filters.serviceAreaId ||
           pendingFilters.contractAreaId !== filters.contractAreaId ||
           pendingFilters.assetType !== filters.assetType;
  };

  // Handle local filter changes (don't apply immediately)
  const handlePendingFilterChange = (name: string, value: string) => {
    setPendingFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    onApplyFilters(pendingFilters);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    const emptyFilters = {
      serviceAreaId: '',
      contractAreaId: '',
      assetType: '',
      search: ''
    };
    setPendingFilters(emptyFilters);
    if (onClearFilters) {
      onClearFilters();
    }
  };

  return (
    <FiltersCard
      title="Asset Filters"
      subtitle="Filter assets by service area, contract area, and type"
      collapsible={true}
      defaultCollapsed={false}
      showApplyButton={true}
      onApply={handleApplyFilters}
      applyDisabled={!hasUnappliedChanges()}
      showClearButton={true}
      onClear={handleClearFilters}
      hasActiveFilters={hasActiveFilters()}
    >
      <Row className="tw-items-end">
        <Col lg={3} md={4} className="tw-mb-3 md:tw-mb-0">
          <StandoutDropdown
            type="searchable"
            options={filterOptions.serviceAreas}
            value={pendingFilters.serviceAreaId}
            onChange={(value) => handlePendingFilterChange('serviceAreaId', value)}
            getValue={(area) => area.id.toString()}
            formatOption={(area) => area.name}
            placeholder="All Service Areas"
            className="tw-w-full"
            label="Service Area"
            noOptionsMessage="No service areas available"
          />
        </Col>
        <Col lg={3} md={4} className="tw-mb-3 md:tw-mb-0">
          <SearchableDropdown
            options={filterOptions.contractAreas}
            value={pendingFilters.contractAreaId}
            onChange={(value) => handlePendingFilterChange('contractAreaId', value)}
            getValue={(area) => area.id.toString()}
            formatOption={(area) => area.name}
            placeholder="All Contract Areas"
            className="tw-w-full"
            label="Contract Area"
            noOptionsMessage="No contract areas available"
          />
        </Col>
        <Col lg={2} md={3} className="tw-mb-3 md:tw-mb-0">
          <Form.Label>Asset Type</Form.Label>
          <Form.Select
            size="sm"
            value={pendingFilters.assetType}
            onChange={(e) => handlePendingFilterChange('assetType', e.target.value)}
          >
            <option value="">All Types</option>
            {filterOptions.assetTypes.map(type => (
              <option key={type} value={type}>
                {ASSET_TYPE_LABELS[type as keyof typeof ASSET_TYPE_LABELS] || type}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col lg={4} md={12} className="tw-mb-3 md:tw-mb-0">
          {/* Action buttons are now handled by FiltersCard */}
        </Col>
      </Row>
    </FiltersCard>
  );
};

export default AssetFilters;